// ─────────────────────────────────────────────────────────────────────────────
// Auth store — Zustand (persisted to localStorage)
// ─────────────────────────────────────────────────────────────────────────────
import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { api } from '@/lib/api'

interface User {
  id: string
  groupId: string
  email: string
  firstName: string
  lastName: string
  role: string
  language: string
  avatarUrl?: string
  department?: string
  propertyAccess: Array<{
    propertyId: string
    isDefault: boolean
    property: { name: string; code: string }
  }>
  group: { id: string; name: string; logoUrl?: string; primaryCurrency: string }
}

interface AuthState {
  user: User | null
  accessToken: string | null
  refreshToken: string | null
  currentPropertyId: string | null
  isAuthenticated: boolean

  login: (email: string, password: string) => Promise<void>
  logout: () => Promise<void>
  setCurrentProperty: (propertyId: string) => void
  refreshAccessToken: () => Promise<void>
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      accessToken: null,
      refreshToken: null,
      currentPropertyId: null,
      isAuthenticated: false,

      login: async (email, password) => {
        const { data } = await api.post('/auth/login', { email, password })
        const defaultProperty = data.user.propertyAccess.find((p: any) => p.isDefault)

        set({
          user: data.user,
          accessToken: data.accessToken,
          refreshToken: data.refreshToken,
          isAuthenticated: true,
          currentPropertyId: defaultProperty?.propertyId ?? data.user.propertyAccess[0]?.propertyId ?? null,
        })

        // Set auth header for future requests
        api.defaults.headers.common['Authorization'] = `Bearer ${data.accessToken}`
      },

      logout: async () => {
        const { refreshToken } = get()
        try {
          if (refreshToken) await api.post('/auth/logout', { refreshToken })
        } catch { /* ignore */ }

        delete api.defaults.headers.common['Authorization']
        set({ user: null, accessToken: null, refreshToken: null, isAuthenticated: false, currentPropertyId: null })
      },

      setCurrentProperty: (propertyId) => set({ currentPropertyId: propertyId }),

      refreshAccessToken: async () => {
        const { refreshToken } = get()
        if (!refreshToken) throw new Error('No refresh token')
        const { data } = await api.post('/auth/refresh', { refreshToken })
        set({ accessToken: data.accessToken })
        api.defaults.headers.common['Authorization'] = `Bearer ${data.accessToken}`
      },
    }),
    {
      name: 'estatekey-auth',
      partialize: (state) => ({
        user: state.user,
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        currentPropertyId: state.currentPropertyId,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
)
